import { useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { InventoryView } from './InventoryView.js';
import { useInventoryQuery } from './useInventoryQuery.js';

/**
 * The `/inventory` route component (promoted from a ComingSoon stub this plan). Reads the typed
 * search params (`category`, `archived`), fires the coarse-endpoint TanStack Query, and renders
 * the faithful `InventoryView` on success. Loading → a page-shaped `RouteSkeleton`; error → announced
 * error state with a retry that refetches (the reports `ReportsRoute` 3-state contract).
 *
 * The server computes the enriched list + per-category counts + low-stock flags; this component
 * never re-aggregates and never sends a tenant id (SEC-11).
 */

const routeApi = getRouteApi('/inventory');

export function InventoryRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = useInventoryQuery(search);

  if (query.isPending) {
    return <RouteSkeleton label={t('inventory:title')} rows={8} columns={5} filters={3} />;
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('inventory:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('inventory:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('inventory:error.retry')}
        </button>
      </section>
    );
  }

  return <InventoryView data={query.data} search={search} />;
}
